import type { ErrorPolicy } from '@apollo/client';
import type { useApolloClient } from '@apollo/client/react';
import type { ExportRowsRequest } from '@nudool/design-system';
import type { DocumentNode } from 'graphql';

import { fetchAllPages } from '../fetch-all-pages.js';
import { buildExportConfirmMessage } from './build-export-confirm-message.js';
import { buildSelectionExportConfirmMessage } from './build-selection-export-confirm-message.js';
import { EXPORT_CONFIRM_ROW_COUNT } from './export-confirm-row-count.js';
import { EXPORT_PAGE_SIZE } from './export-page-size.js';
import type { ListTableConnection } from './list-table-connection.js';
import { nullifyUndefined } from './nullify-undefined.js';
import { STALE_EXPORT_MESSAGE } from './stale-export-message.js';

// Shown when a resolver doesn't populate totalCount -- an unknown count is not evidence of a small export.
const UNKNOWN_ROW_COUNT_EXPORT_CONFIRM_MESSAGE =
  'Row count unknown — this export may be large. Continue?';
const UNKNOWN_ROW_COUNT_SELECTION_EXPORT_CONFIRM_MESSAGE =
  'Row count unknown — finding your selected rows means scanning every row, which may take a while. Continue?';

type CreateExportRowsFetcherOptions<TData, TNode> = {
  client: ReturnType<typeof useApolloClient>;
  query: DocumentNode;
  // Row-selection variables only, with no cursor -- fetchAllPages owns `first`/`after`.
  variables: Record<string, unknown>;
  extractConnection: (data: TData | undefined) => ListTableConnection<TNode> | undefined;
  totalCount: number | undefined;
  // Renders a real dialog rather than `window.confirm`, which enterprise browser policy can silently no-op to `true`.
  confirmLargeExport: (message: string) => Promise<boolean>;
  // The on-screen variables as of now, so an export whose view changed mid-flight aborts instead of downloading stale rows.
  readLatestVariables: () => Record<string, unknown>;
  errorPolicy?: ErrorPolicy;
};

// A selection export still walks the whole filtered dataset to find its rows, so it confirms on the same thresholds as a full export.
function resolveConfirmMessage(
  totalCount: number | undefined,
  selectedRowCount: number | undefined,
): string | undefined {
  if (totalCount === undefined) {
    return selectedRowCount === undefined
      ? UNKNOWN_ROW_COUNT_EXPORT_CONFIRM_MESSAGE
      : UNKNOWN_ROW_COUNT_SELECTION_EXPORT_CONFIRM_MESSAGE;
  }
  if (totalCount <= EXPORT_CONFIRM_ROW_COUNT) return undefined;
  return selectedRowCount === undefined
    ? buildExportConfirmMessage(totalCount)
    : buildSelectionExportConfirmMessage(selectedRowCount, totalCount);
}

/** Returns null -- an abort, not an empty dataset -- when the row count is over the confirm ceiling and the person declines. */
export function createExportRowsFetcher<TData, TNode>({
  client,
  query,
  variables,
  extractConnection,
  totalCount,
  confirmLargeExport,
  readLatestVariables,
  errorPolicy,
}: CreateExportRowsFetcherOptions<TData, TNode>): (
  options?: ExportRowsRequest,
) => Promise<TNode[] | null> {
  const requestedVariables = JSON.stringify(variables, nullifyUndefined);
  function assertViewUnchanged() {
    if (JSON.stringify(readLatestVariables(), nullifyUndefined) !== requestedVariables) {
      throw new Error(STALE_EXPORT_MESSAGE);
    }
  }

  return async (options) => {
    const confirmMessage = resolveConfirmMessage(totalCount, options?.selectedRowCount);
    if (confirmMessage !== undefined && !(await confirmLargeExport(confirmMessage))) return null;
    assertViewUnchanged();
    const rows = await fetchAllPages<TData, { first: number; after?: string | null }, TNode>(
      client,
      {
        query,
        variables,
        pageSize: EXPORT_PAGE_SIZE,
        extractConnection: (data) => extractConnection(data),
        fetchPolicy: 'no-cache',
        errorPolicy,
        signal: options?.signal,
      },
    );
    assertViewUnchanged();
    return rows;
  };
}
